import * as actionTypes from '../../constants/actionTypes';
import { DELETE, GET, POST, PUT, PATCH } from 'js/constants/requestMethods';
import { CALL_GENAI_API, CALL_PRIVATE_API } from 'js/middleware/api';
import {
  aiToolSchema,
  aiToolReceiptSchema,
  aiToolPackageSchema,
  aiToolPolicySchema,
  aiToolRateLimitSchema,
  aiToolsetExportSchema,
} from 'js/schemas/aiTool';
import { dataSetSchema } from 'js/schemas';
import Constants from 'js/Constants';
import { processSearchFilters } from 'js/lib/SearchFilterUtils';
import {
  ACLQueryModeTypes,
  AvailableFilterTypesByView,
  SearchFilterOpTypes,
  SearchFilterTypes,
  SearchFilterViews,
} from 'js/constants/visibilityFilters';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import {
  ToolKind,
  ActorType,
  ResultStatus,
  PolicyDecision,
  ToolsetExportType,
  ToolsetEnvironment,
  PolicyEngine,
  RateLimitScope,
  ReconcileMode,
} from 'new/modules/ai/types';

// ============================================================================
// Types
// ============================================================================

export interface CreateToolPayload {
  name: string;
  description?: string;
  data_set_id: number;
  kind: ToolKind;
}

export interface UpdateToolPayload {
  name?: string;
  description?: string;
}

export interface CreateReceiptPayload {
  actor_type: ActorType;
  result_status: ResultStatus;
  auth_type?: string;
  policy_decision?: PolicyDecision;
  subject_id?: string;
  policy_id?: string;
  trace_id?: string;
  receipt?: Record<string, any>;
}

export interface GenerateToolsPayload {
  org_id?: number;
  selector: {
    dataset_ids?: number[];
    tags?: string[];
    domains?: string[];
  };
}

export interface ToolCallPayload {
  tool_name: string;
  arguments: Record<string, any>;
}

export interface ReconcileToolsPayload {
  mode: ReconcileMode;
  since?: string; // ISO timestamp, required if mode is 'changed_since'
}

export interface PackageToolItem {
  tool_id: number; // Direct tool ID (versionless)
}

export interface CreateToolFromNexsetPayload {
  nexset_id: number;
  publish?: boolean;
}

export interface CreateToolsetPayload {
  name: string;
  key?: string;
  description?: string;
  tool_ids?: number[]; // Direct tool IDs (versionless)
  create_export?: boolean; // Auto-creates MCP export (default: true)
}

export interface CreateToolsetFromNexsetsPayload {
  name: string;
  description?: string;
  nexset_ids: number[];
}

export interface UpdateToolsetPayload {
  name?: string;
  display_name?: string; // Deprecated alias for name
  description?: string;
  tags?: string[];
  owner_id?: number | null;
  manifest?: Record<string, any>;
  manifest_hash?: string;
  tools_hash?: string;
}

export interface CreateToolsetExportPayload {
  export_type: ToolsetExportType;
  environment: ToolsetEnvironment;
  public_name?: string;
  description?: string;
  export_metadata?: Record<string, any>;
  endpoint_url?: string;
}

export interface CreatePackagePayload {
  name: string;
  description: string;
  tools: PackageToolItem[];
}

export interface UpdatePackagePayload {
  name?: string;
  description?: string;
  tools?: PackageToolItem[];
  status?: 'active' | 'inactive';
}

export interface CreatePolicyPayload {
  name: string;
  description?: string;
  engine: PolicyEngine;
  policy_rego: string;
}

export interface UpdatePolicyPayload {
  name?: string;
  description?: string;
  policy_rego?: string;
  status?: 'active' | 'inactive';
}

export interface CreateRateLimitPayload {
  name: string;
  scope: RateLimitScope;
  config: {
    requests_per_minute: number;
    burst?: number;
    window_seconds?: number;
  };
}

export interface UpdateRateLimitPayload {
  name?: string;
  config?: {
    requests_per_minute?: number;
    burst?: number;
    window_seconds?: number;
  };
}

// ============================================================================
// Tool Actions
// ============================================================================

/**
 * Fetch all tools for the current org
 * GET /tools
 * Query params: limit (1-200), offset, status (PUBLISHED, DRAFT, DEPRECATED, RETIRED)
 */
export function fetchAITools(params?: {
  limit?: number;
  offset?: number;
  status?: 'init' | 'paused' | 'active';
  // Legacy support
  page?: number;
  per_page?: number;
}) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    const shouldAppend =
      (params?.offset !== undefined && params.offset > 0) || (params?.page && params.page > 1);

    // Use limit/offset (Postman format) or convert from page/per_page
    if (params?.limit) {
      queryParams.set('limit', params.limit.toString());
    } else if (params?.per_page) {
      queryParams.set('limit', params.per_page.toString());
    }

    if (params?.offset !== undefined) {
      queryParams.set('offset', params.offset.toString());
    } else if (params?.page && params?.per_page) {
      queryParams.set('offset', ((params.page - 1) * params.per_page).toString());
    }

    if (params?.status) {
      queryParams.set('status', params.status);
    }

    const endpoint = `/tools${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOLS_REQUEST,
          actionTypes.GET_AI_TOOLS_SUCCESS,
          actionTypes.GET_AI_TOOLS_FAILURE,
        ],
        endpoint,
        schema: [aiToolSchema],
        requestMethod: GET,
        requestInfo: {
          ...params,
          append: shouldAppend,
        },
      },
    });
  };
}

/**
 * Fetch a single tool by ID or key
 * GET /tools/{id}
 */
export function fetchAITool(idOrKey: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_REQUEST,
          actionTypes.GET_AI_TOOL_SUCCESS,
          actionTypes.GET_AI_TOOL_FAILURE,
        ],
        endpoint: `/tools/${idOrKey}`,
        schema: aiToolSchema,
        requestMethod: GET,
      },
    });
  };
}

/**
 * Create a new tool
 * POST /tools
 */
export function createAITool(data: CreateToolPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_REQUEST,
          actionTypes.CREATE_AI_TOOL_SUCCESS,
          actionTypes.CREATE_AI_TOOL_FAILURE,
        ],
        endpoint: '/tools',
        schema: aiToolSchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Create a tool from a Nexset
 * POST /tools:from_nexset
 */
export function createAIToolFromNexset(data: CreateToolFromNexsetPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_REQUEST,
          actionTypes.CREATE_AI_TOOL_SUCCESS,
          actionTypes.CREATE_AI_TOOL_FAILURE,
        ],
        endpoint: '/tools:from_nexset',
        schema: aiToolSchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Update a tool
 * PATCH /tools/{id}
 * Note: Based on Postman collection, toolsets use PATCH, so tools likely do too
 */
export function updateAITool(id: number | string, data: UpdateToolPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.UPDATE_AI_TOOL_REQUEST,
          actionTypes.UPDATE_AI_TOOL_SUCCESS,
          actionTypes.UPDATE_AI_TOOL_FAILURE,
        ],
        endpoint: `/tools/${id}`,
        schema: aiToolSchema,
        requestMethod: PATCH,
        payload: data,
      },
    });
  };
}

/**
 * Delete a tool
 * DELETE /tools/{id}
 */
export function deleteAITool(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.DELETE_AI_TOOL_REQUEST,
          actionTypes.DELETE_AI_TOOL_SUCCESS,
          actionTypes.DELETE_AI_TOOL_FAILURE,
        ],
        endpoint: `/tools/${id}`,
        requestMethod: DELETE,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Get tool definition (from latest published revision)
 * GET /tools/{id}/definition
 */
export function fetchAIToolDefinition(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_DEFINITION_REQUEST,
          actionTypes.GET_AI_TOOL_DEFINITION_SUCCESS,
          actionTypes.GET_AI_TOOL_DEFINITION_FAILURE,
        ],
        endpoint: `/tools/${id}/definition`,
        requestMethod: GET,
      },
    });
  };
}

/**
 * Generate tools from Nexla entities (datasets/nexsets)
 * POST /tools/generate
 */
export function generateAITools(data: GenerateToolsPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GENERATE_AI_TOOLS_REQUEST,
          actionTypes.GENERATE_AI_TOOLS_SUCCESS,
          actionTypes.GENERATE_AI_TOOLS_FAILURE,
        ],
        endpoint: '/tools/generate',
        schema: [aiToolSchema],
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Call/execute a tool via MCP protocol
 * POST /tools:call
 * @deprecated Use executeAITool for REST endpoint.
 */
export function callAITool(data: ToolCallPayload) {
  console.warn('callAITool is deprecated. Use executeAITool for REST endpoint.');
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CALL_AI_TOOL_REQUEST,
          actionTypes.CALL_AI_TOOL_SUCCESS,
          actionTypes.CALL_AI_TOOL_FAILURE,
        ],
        endpoint: '/tools:call',
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Execute a tool directly via REST (versionless)
 * POST /tools/{id}:execute
 */
export interface ExecuteToolPayload {
  args: Record<string, any>; // Arguments validated against tool input schema
}

export function executeAITool(id: number | string, data: ExecuteToolPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CALL_AI_TOOL_REQUEST,
          actionTypes.CALL_AI_TOOL_SUCCESS,
          actionTypes.CALL_AI_TOOL_FAILURE,
        ],
        endpoint: `/tools/${id}:execute`,
        requestMethod: POST,
        payload: data,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Test a tool (dry run)
 * POST /tools/{id}/test
 */
export function testAITool(idOrKey: string | number, args?: Record<string, any>) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.TEST_AI_TOOL_REQUEST,
          actionTypes.TEST_AI_TOOL_SUCCESS,
          actionTypes.TEST_AI_TOOL_FAILURE,
        ],
        endpoint: `/tools/${idOrKey}/test`,
        requestMethod: POST,
        payload: { arguments: args || {} },
        requestInfo: { idOrKey },
      },
    });
  };
}

/**
 * Reconcile tools - update tool definitions when source datasets changed
 * POST /tools:reconcile
 */
export function reconcileAITools(data: ReconcileToolsPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.RECONCILE_AI_TOOLS_REQUEST,
          actionTypes.RECONCILE_AI_TOOLS_SUCCESS,
          actionTypes.RECONCILE_AI_TOOLS_FAILURE,
        ],
        endpoint: '/tools:reconcile',
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

// ============================================================================
// Tool Lifecycle Actions (versionless)
// ============================================================================

/**
 * Activate a tool
 * POST /tools/{id}:activate
 */
export function activateAITool(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.ACTIVATE_AI_TOOL_REQUEST,
          actionTypes.ACTIVATE_AI_TOOL_SUCCESS,
          actionTypes.ACTIVATE_AI_TOOL_FAILURE,
        ],
        endpoint: `/tools/${id}:activate`,
        schema: aiToolSchema,
        requestMethod: POST,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Pause a tool
 * POST /tools/{id}:pause
 */
export function pauseAITool(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.PAUSE_AI_TOOL_REQUEST,
          actionTypes.PAUSE_AI_TOOL_SUCCESS,
          actionTypes.PAUSE_AI_TOOL_FAILURE,
        ],
        endpoint: `/tools/${id}:pause`,
        schema: aiToolSchema,
        requestMethod: POST,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Fetch tool changelogs (audit trail)
 * GET /tools/{id}/changelogs
 */
export function fetchAIToolChangelogs(
  toolId: number | string,
  params?: { limit?: number; offset?: number },
) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.set('offset', params.offset.toString());

    const endpoint = `/tools/${toolId}/changelogs${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_CHANGELOGS_REQUEST,
          actionTypes.GET_AI_TOOL_CHANGELOGS_SUCCESS,
          actionTypes.GET_AI_TOOL_CHANGELOGS_FAILURE,
        ],
        endpoint,
        requestMethod: GET,
        requestInfo: { toolId },
      },
    });
  };
}

// ============================================================================
// Receipt Actions
// ============================================================================

/**
 * Fetch receipts for a tool
 * GET /tools/{toolId}/receipts
 */
export function fetchAIToolReceipts(
  toolId: number | string,
  params?: { limit?: number; offset?: number },
) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) {
      queryParams.set('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.set('offset', params.offset.toString());
    }

    const endpoint = `/tools/${toolId}/receipts${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_RECEIPTS_REQUEST,
          actionTypes.GET_AI_TOOL_RECEIPTS_SUCCESS,
          actionTypes.GET_AI_TOOL_RECEIPTS_FAILURE,
        ],
        endpoint,
        schema: [aiToolReceiptSchema],
        requestMethod: GET,
        requestInfo: { toolId },
      },
    });
  };
}

/**
 * Fetch all receipts (top-level)
 * GET /receipts
 */
export function fetchAllAIToolReceipts(params?: {
  receipt_id?: string;
  tool_id?: number;
  principal?: string;
  from?: string; // ISO timestamp
  to?: string; // ISO timestamp
  result_status?: 'ok' | 'error';
  policy_decision?: string;
  limit?: number;
  offset?: number;
}) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    if (params?.receipt_id) queryParams.set('receipt_id', params.receipt_id);
    if (params?.tool_id) queryParams.set('tool_id', params.tool_id.toString());
    if (params?.principal) queryParams.set('principal', params.principal);
    if (params?.from) queryParams.set('from', params.from);
    if (params?.to) queryParams.set('to', params.to);
    if (params?.result_status) queryParams.set('result_status', params.result_status);
    if (params?.policy_decision) queryParams.set('policy_decision', params.policy_decision);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.set('offset', params.offset.toString());

    const endpoint = `/receipts${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_RECEIPTS_REQUEST,
          actionTypes.GET_AI_TOOL_RECEIPTS_SUCCESS,
          actionTypes.GET_AI_TOOL_RECEIPTS_FAILURE,
        ],
        endpoint,
        schema: [aiToolReceiptSchema],
        requestMethod: GET,
      },
    });
  };
}

/**
 * Fetch a specific receipt by ID or UUID (with tool ID)
 * GET /tools/{toolId}/receipts/{receiptIdOrUuid}
 */
export function fetchAIToolReceipt(toolId: number | string, receiptIdOrUuid: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_RECEIPT_REQUEST,
          actionTypes.GET_AI_TOOL_RECEIPT_SUCCESS,
          actionTypes.GET_AI_TOOL_RECEIPT_FAILURE,
        ],
        endpoint: `/tools/${toolId}/receipts/${receiptIdOrUuid}`,
        schema: aiToolReceiptSchema,
        requestMethod: GET,
        requestInfo: { toolId, receiptIdOrUuid },
      },
    });
  };
}

/**
 * Fetch a receipt by ID or UUID (direct endpoint from Postman)
 * GET /receipts/{receiptIdOrUuid}
 */
export function fetchAIReceipt(receiptIdOrUuid: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_RECEIPT_REQUEST,
          actionTypes.GET_AI_TOOL_RECEIPT_SUCCESS,
          actionTypes.GET_AI_TOOL_RECEIPT_FAILURE,
        ],
        endpoint: `/receipts/${receiptIdOrUuid}`,
        schema: aiToolReceiptSchema,
        requestMethod: GET,
        requestInfo: { receiptIdOrUuid },
      },
    });
  };
}

/**
 * Create a receipt for a tool (versionless)
 * POST /tools/{toolId}/receipts
 */
export function createAIToolReceipt(toolId: number | string, data: CreateReceiptPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_RECEIPT_REQUEST,
          actionTypes.CREATE_AI_TOOL_RECEIPT_SUCCESS,
          actionTypes.CREATE_AI_TOOL_RECEIPT_FAILURE,
        ],
        endpoint: `/tools/${toolId}/receipts`,
        schema: aiToolReceiptSchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

// ============================================================================
// Package Actions
// ============================================================================

/**
 * Fetch all tool packages
 * GET /tool-packages
 */
export function fetchAIToolPackages(params?: { status?: string; limit?: number; offset?: number }) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    if (params?.status) {
      queryParams.set('status', params.status);
    }
    if (params?.limit) {
      queryParams.set('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.set('offset', params.offset.toString());
    }

    const endpoint = `/tool-packages${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_PACKAGES_REQUEST,
          actionTypes.GET_AI_TOOL_PACKAGES_SUCCESS,
          actionTypes.GET_AI_TOOL_PACKAGES_FAILURE,
        ],
        endpoint,
        schema: [aiToolPackageSchema],
        requestMethod: GET,
      },
    });
  };
}

/**
 * Fetch a single tool package (returns manifest for MCP server)
 * GET /tool-packages/{id}
 */
export function fetchAIToolPackage(id: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_PACKAGE_REQUEST,
          actionTypes.GET_AI_TOOL_PACKAGE_SUCCESS,
          actionTypes.GET_AI_TOOL_PACKAGE_FAILURE,
        ],
        endpoint: `/tool-packages/${id}`,
        schema: aiToolPackageSchema,
        requestMethod: GET,
      },
    });
  };
}

/**
 * Create a tool package
 * POST /tool-packages
 */
export function createAIToolPackage(data: CreatePackagePayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_PACKAGE_REQUEST,
          actionTypes.CREATE_AI_TOOL_PACKAGE_SUCCESS,
          actionTypes.CREATE_AI_TOOL_PACKAGE_FAILURE,
        ],
        endpoint: '/tool-packages',
        schema: aiToolPackageSchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Create a toolset (versionless)
 * POST /toolsets
 */
export function createAIToolset(data: CreateToolsetPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_PACKAGE_REQUEST,
          actionTypes.CREATE_AI_TOOL_PACKAGE_SUCCESS,
          actionTypes.CREATE_AI_TOOL_PACKAGE_FAILURE,
        ],
        endpoint: '/toolsets',
        schema: aiToolPackageSchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Create a toolset directly from Nexset IDs
 * POST /toolsets:from_nexsets
 * Auto-generates tools and creates an MCP export in one call
 */
export function createToolsetFromNexsets(data: CreateToolsetFromNexsetsPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_PACKAGE_REQUEST,
          actionTypes.CREATE_AI_TOOL_PACKAGE_SUCCESS,
          actionTypes.CREATE_AI_TOOL_PACKAGE_FAILURE,
        ],
        endpoint: '/toolsets:from_nexsets',
        schema: aiToolPackageSchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * List toolsets for the organization
 * GET /toolsets
 */
export function fetchAIToolsets(params?: {
  visibility?: 'private' | 'org' | 'public';
  limit?: number;
  offset?: number;
}) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    const shouldAppend = params?.offset !== undefined && params.offset > 0;
    if (params?.visibility) queryParams.set('visibility', params.visibility);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.set('offset', params.offset.toString());

    const endpoint = `/toolsets${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOLSETS_REQUEST,
          actionTypes.GET_AI_TOOLSETS_SUCCESS,
          actionTypes.GET_AI_TOOLSETS_FAILURE,
        ],
        endpoint,
        schema: [aiToolPackageSchema],
        requestMethod: GET,
        requestInfo: {
          ...params,
          append: shouldAppend,
        },
      },
    });
  };
}

/**
 * Get a toolset (versionless)
 * GET /toolsets/{id}
 */
export function fetchAIToolset(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOLSET_REQUEST,
          actionTypes.GET_AI_TOOLSET_SUCCESS,
          actionTypes.GET_AI_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${id}`,
        schema: aiToolPackageSchema,
        requestMethod: GET,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Update a toolset
 * PATCH /toolsets/{id}
 */
export function updateAIToolset(id: number | string, data: UpdateToolsetPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.UPDATE_AI_TOOLSET_REQUEST,
          actionTypes.UPDATE_AI_TOOLSET_SUCCESS,
          actionTypes.UPDATE_AI_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${id}`,
        schema: aiToolPackageSchema,
        requestMethod: PATCH,
        payload: data,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Activate a toolset
 * POST /toolsets/{id}:activate
 */
export function activateAIToolset(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.ACTIVATE_AI_TOOLSET_REQUEST,
          actionTypes.ACTIVATE_AI_TOOLSET_SUCCESS,
          actionTypes.ACTIVATE_AI_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${id}:activate`,
        schema: aiToolPackageSchema,
        requestMethod: POST,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Pause a toolset
 * POST /toolsets/{id}:pause
 */
export function pauseAIToolset(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.PAUSE_AI_TOOLSET_REQUEST,
          actionTypes.PAUSE_AI_TOOLSET_SUCCESS,
          actionTypes.PAUSE_AI_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${id}:pause`,
        schema: aiToolPackageSchema,
        requestMethod: POST,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Disable a toolset
 * POST /toolsets/{id}:disable
 */
export function disableAIToolset(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.DISABLE_AI_TOOLSET_REQUEST,
          actionTypes.DISABLE_AI_TOOLSET_SUCCESS,
          actionTypes.DISABLE_AI_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${id}:disable`,
        schema: aiToolPackageSchema,
        requestMethod: POST,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Enable a toolset
 * POST /toolsets/{id}:enable
 */
export function enableAIToolset(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.ENABLE_AI_TOOLSET_REQUEST,
          actionTypes.ENABLE_AI_TOOLSET_SUCCESS,
          actionTypes.ENABLE_AI_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${id}:enable`,
        schema: aiToolPackageSchema,
        requestMethod: POST,
        requestInfo: { id },
      },
    });
  };
}

/**
 * Delete a toolset
 * DELETE /toolsets/{id}
 */
export function deleteAIToolset(id: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.DELETE_AI_TOOLSET_REQUEST,
          actionTypes.DELETE_AI_TOOLSET_SUCCESS,
          actionTypes.DELETE_AI_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${id}`,
        requestMethod: DELETE,
        requestInfo: { id },
      },
    });
  };
}

// ============================================================================
// Toolset Changelog Actions (versionless audit trail)
// ============================================================================

/**
 * Fetch changelogs for a toolset
 * GET /toolsets/{toolsetId}/changelogs
 */
export function fetchAIToolsetChangelogs(
  toolsetId: number | string,
  params?: {
    change_type?: 'created' | 'updated' | 'tools_added' | 'tools_removed' | 'activated' | 'paused';
    limit?: number;
    offset?: number;
  },
) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    if (params?.change_type) queryParams.set('change_type', params.change_type);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.set('offset', params.offset.toString());

    const endpoint = `/toolsets/${toolsetId}/changelogs${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOLSET_CHANGELOGS_REQUEST,
          actionTypes.GET_AI_TOOLSET_CHANGELOGS_SUCCESS,
          actionTypes.GET_AI_TOOLSET_CHANGELOGS_FAILURE,
        ],
        endpoint,
        requestMethod: GET,
        requestInfo: { toolsetId },
      },
    });
  };
}

// ============================================================================
// Toolset Tool Membership Actions
// ============================================================================

/**
 * Add tools to a toolset
 * POST /toolsets/{toolsetId}/tools
 */
export function addToolsToToolset(toolsetId: number | string, data: { tool_ids: number[] }) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.ADD_TOOLS_TO_TOOLSET_REQUEST,
          actionTypes.ADD_TOOLS_TO_TOOLSET_SUCCESS,
          actionTypes.ADD_TOOLS_TO_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${toolsetId}/tools`,
        schema: aiToolPackageSchema,
        requestMethod: POST,
        payload: data,
        requestInfo: { toolsetId },
      },
    });
  };
}

/**
 * Remove tools from a toolset
 * DELETE /toolsets/{toolsetId}/tools
 */
export function removeToolsFromToolset(toolsetId: number | string, data: { tool_ids: number[] }) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.REMOVE_TOOLS_FROM_TOOLSET_REQUEST,
          actionTypes.REMOVE_TOOLS_FROM_TOOLSET_SUCCESS,
          actionTypes.REMOVE_TOOLS_FROM_TOOLSET_FAILURE,
        ],
        endpoint: `/toolsets/${toolsetId}/tools`,
        schema: aiToolPackageSchema,
        requestMethod: DELETE,
        payload: data,
        requestInfo: { toolsetId },
      },
    });
  };
}

// ============================================================================
// Toolset Export Actions
// ============================================================================

/**
 * Create an export for a toolset (versionless)
 * POST /toolsets/{toolsetId}/exports
 */
export function createAIToolsetExport(
  toolsetId: number | string,
  data: CreateToolsetExportPayload,
) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOLSET_EXPORT_REQUEST,
          actionTypes.CREATE_AI_TOOLSET_EXPORT_SUCCESS,
          actionTypes.CREATE_AI_TOOLSET_EXPORT_FAILURE,
        ],
        endpoint: `/toolsets/${toolsetId}/exports`,
        schema: aiToolsetExportSchema,
        requestMethod: POST,
        payload: data,
        requestInfo: { toolsetId },
      },
    });
  };
}

/**
 * List all exports for the organization
 * GET /exports
 */
export function fetchAIToolsetExports(params?: {
  export_type?: 'mcp_server' | 'copilot_connector' | 'sdk_bundle' | 'http_catalog';
  limit?: number;
  offset?: number;
}) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    const shouldAppend = params?.offset !== undefined && params.offset > 0;
    if (params?.export_type) queryParams.set('export_type', params.export_type);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.set('offset', params.offset.toString());

    const endpoint = `/exports${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOLSET_EXPORTS_REQUEST,
          actionTypes.GET_AI_TOOLSET_EXPORTS_SUCCESS,
          actionTypes.GET_AI_TOOLSET_EXPORTS_FAILURE,
        ],
        endpoint,
        schema: [aiToolsetExportSchema],
        requestMethod: GET,
        requestInfo: {
          export_type: params?.export_type,
          limit: params?.limit,
          offset: params?.offset,
          append: shouldAppend,
        },
      },
    });
  };
}

/**
 * Get a specific export by ID
 * GET /exports/{exportId}
 */
export function fetchAIToolsetExport(exportId: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOLSET_EXPORT_REQUEST,
          actionTypes.GET_AI_TOOLSET_EXPORT_SUCCESS,
          actionTypes.GET_AI_TOOLSET_EXPORT_FAILURE,
        ],
        endpoint: `/exports/${exportId}`,
        schema: aiToolsetExportSchema,
        requestMethod: GET,
        requestInfo: { exportId },
      },
    });
  };
}

/**
 * Delete a toolset export (MCP server)
 * DELETE /exports/{exportId}
 */
export function deleteAIToolsetExport(exportId: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.DELETE_AI_TOOLSET_EXPORT_REQUEST,
          actionTypes.DELETE_AI_TOOLSET_EXPORT_SUCCESS,
          actionTypes.DELETE_AI_TOOLSET_EXPORT_FAILURE,
        ],
        endpoint: `/exports/${exportId}`,
        requestMethod: DELETE,
        requestInfo: { exportId },
      },
    });
  };
}

/**
 * Update payload for toolset export
 */
export interface UpdateToolsetExportPayload {
  public_name?: string;
  description?: string;
  export_metadata?: Record<string, any>;
}

/**
 * Update a toolset export (MCP server)
 * PATCH /exports/{exportId}
 */
export function updateAIToolsetExport(exportId: number | string, data: UpdateToolsetExportPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.UPDATE_AI_TOOLSET_EXPORT_REQUEST,
          actionTypes.UPDATE_AI_TOOLSET_EXPORT_SUCCESS,
          actionTypes.UPDATE_AI_TOOLSET_EXPORT_FAILURE,
        ],
        endpoint: `/exports/${exportId}`,
        schema: aiToolsetExportSchema,
        requestMethod: PATCH,
        payload: data,
        requestInfo: { exportId },
      },
    });
  };
}

/**
 * Activate a toolset export (MCP server)
 * POST /exports/{exportId}:activate
 */
export function activateAIToolsetExport(exportId: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.ACTIVATE_AI_TOOLSET_EXPORT_REQUEST,
          actionTypes.ACTIVATE_AI_TOOLSET_EXPORT_SUCCESS,
          actionTypes.ACTIVATE_AI_TOOLSET_EXPORT_FAILURE,
        ],
        endpoint: `/exports/${exportId}:activate`,
        schema: aiToolsetExportSchema,
        requestMethod: POST,
        requestInfo: { exportId },
      },
    });
  };
}

/**
 * Pause a toolset export (MCP server)
 * POST /exports/{exportId}:pause
 */
export function pauseAIToolsetExport(exportId: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.PAUSE_AI_TOOLSET_EXPORT_REQUEST,
          actionTypes.PAUSE_AI_TOOLSET_EXPORT_SUCCESS,
          actionTypes.PAUSE_AI_TOOLSET_EXPORT_FAILURE,
        ],
        endpoint: `/exports/${exportId}:pause`,
        schema: aiToolsetExportSchema,
        requestMethod: POST,
        requestInfo: { exportId },
      },
    });
  };
}

// ============================================================================
// MCP Config Actions
// ============================================================================

/**
 * Get MCP config by toolset ID (versionless)
 * GET /toolsets/{toolsetId}/mcp_config
 */
export function fetchAIToolsetMCPConfig(toolsetId: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_MCP_CONFIG_REQUEST,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_SUCCESS,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_FAILURE,
        ],
        endpoint: `/toolsets/${toolsetId}/mcp_config`,
        requestMethod: GET,
        requestInfo: { toolsetId },
      },
    });
  };
}

/**
 * Get MCP config by server key
 * GET /mcp/{serverKey}/config
 */
export function fetchAIToolsetMCPConfigByServerKey(serverKey: string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_MCP_CONFIG_REQUEST,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_SUCCESS,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_FAILURE,
        ],
        endpoint: `/mcp/${serverKey}/config`,
        requestMethod: GET,
        requestInfo: { serverKey },
      },
    });
  };
}

/**
 * Get MCP config by export ID
 * GET /exports/{exportId}/mcp_config
 */
export function fetchAIToolsetMCPConfigByExport(exportId: number | string) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_MCP_CONFIG_REQUEST,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_SUCCESS,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_FAILURE,
        ],
        endpoint: `/exports/${exportId}/mcp_config`,
        requestMethod: GET,
        requestInfo: { exportId },
      },
    });
  };
}

/**
 * Update a tool package (add/remove tools, update metadata)
 * PATCH /tool-packages/{id}
 */
export function updateAIToolPackage(id: string | number, data: UpdatePackagePayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.UPDATE_AI_TOOL_PACKAGE_REQUEST,
          actionTypes.UPDATE_AI_TOOL_PACKAGE_SUCCESS,
          actionTypes.UPDATE_AI_TOOL_PACKAGE_FAILURE,
        ],
        endpoint: `/tool-packages/${id}`,
        schema: aiToolPackageSchema,
        requestMethod: PATCH,
        payload: data,
      },
    });
  };
}

/**
 * Delete a tool package
 * DELETE /tool-packages/{id}
 */
export function deleteAIToolPackage(id: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.DELETE_AI_TOOL_PACKAGE_REQUEST,
          actionTypes.DELETE_AI_TOOL_PACKAGE_SUCCESS,
          actionTypes.DELETE_AI_TOOL_PACKAGE_FAILURE,
        ],
        endpoint: `/tool-packages/${id}`,
        requestMethod: DELETE,
        requestInfo: { id },
      },
    });
  };
}

// ============================================================================
// Policy Actions
// ============================================================================

/**
 * Fetch all policies
 * GET /tools/policies
 */
export function fetchAIToolPolicies(params?: { status?: string; limit?: number; offset?: number }) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    if (params?.status) {
      queryParams.set('status', params.status);
    }
    if (params?.limit) {
      queryParams.set('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.set('offset', params.offset.toString());
    }

    const endpoint = `/tools/policies${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_POLICIES_REQUEST,
          actionTypes.GET_AI_TOOL_POLICIES_SUCCESS,
          actionTypes.GET_AI_TOOL_POLICIES_FAILURE,
        ],
        endpoint,
        schema: [aiToolPolicySchema],
        requestMethod: GET,
      },
    });
  };
}

/**
 * Fetch a single policy
 * GET /tools/policies/{id}
 */
export function fetchAIToolPolicy(id: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_POLICY_REQUEST,
          actionTypes.GET_AI_TOOL_POLICY_SUCCESS,
          actionTypes.GET_AI_TOOL_POLICY_FAILURE,
        ],
        endpoint: `/tools/policies/${id}`,
        schema: aiToolPolicySchema,
        requestMethod: GET,
      },
    });
  };
}

/**
 * Create a policy
 * POST /tools/policies
 */
export function createAIToolPolicy(data: CreatePolicyPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_POLICY_REQUEST,
          actionTypes.CREATE_AI_TOOL_POLICY_SUCCESS,
          actionTypes.CREATE_AI_TOOL_POLICY_FAILURE,
        ],
        endpoint: '/tools/policies',
        schema: aiToolPolicySchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Update a policy
 * PATCH /tools/policies/{id}
 */
export function updateAIToolPolicy(id: string | number, data: UpdatePolicyPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.UPDATE_AI_TOOL_POLICY_REQUEST,
          actionTypes.UPDATE_AI_TOOL_POLICY_SUCCESS,
          actionTypes.UPDATE_AI_TOOL_POLICY_FAILURE,
        ],
        endpoint: `/tools/policies/${id}`,
        schema: aiToolPolicySchema,
        requestMethod: PATCH,
        payload: data,
      },
    });
  };
}

/**
 * Delete a policy
 * DELETE /tools/policies/{id}
 */
export function deleteAIToolPolicy(id: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.DELETE_AI_TOOL_POLICY_REQUEST,
          actionTypes.DELETE_AI_TOOL_POLICY_SUCCESS,
          actionTypes.DELETE_AI_TOOL_POLICY_FAILURE,
        ],
        endpoint: `/tools/policies/${id}`,
        requestMethod: DELETE,
        requestInfo: { id },
      },
    });
  };
}

// ============================================================================
// Rate Limit Actions
// ============================================================================

/**
 * Fetch all rate limits
 * GET /tools/rate-limits
 */
export function fetchAIToolRateLimits(params?: {
  scope?: string;
  limit?: number;
  offset?: number;
}) {
  return (dispatch: any) => {
    const queryParams = new URLSearchParams();
    if (params?.scope) {
      queryParams.set('scope', params.scope);
    }
    if (params?.limit) {
      queryParams.set('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.set('offset', params.offset.toString());
    }

    const endpoint = `/tools/rate-limits${queryParams.toString() ? `?${queryParams}` : ''}`;

    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_RATE_LIMITS_REQUEST,
          actionTypes.GET_AI_TOOL_RATE_LIMITS_SUCCESS,
          actionTypes.GET_AI_TOOL_RATE_LIMITS_FAILURE,
        ],
        endpoint,
        schema: [aiToolRateLimitSchema],
        requestMethod: GET,
      },
    });
  };
}

/**
 * Fetch a single rate limit
 * GET /tools/rate-limits/{id}
 */
export function fetchAIToolRateLimit(id: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_RATE_LIMIT_REQUEST,
          actionTypes.GET_AI_TOOL_RATE_LIMIT_SUCCESS,
          actionTypes.GET_AI_TOOL_RATE_LIMIT_FAILURE,
        ],
        endpoint: `/tools/rate-limits/${id}`,
        schema: aiToolRateLimitSchema,
        requestMethod: GET,
      },
    });
  };
}

/**
 * Create a rate limit
 * POST /tools/rate-limits
 */
export function createAIToolRateLimit(data: CreateRateLimitPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.CREATE_AI_TOOL_RATE_LIMIT_REQUEST,
          actionTypes.CREATE_AI_TOOL_RATE_LIMIT_SUCCESS,
          actionTypes.CREATE_AI_TOOL_RATE_LIMIT_FAILURE,
        ],
        endpoint: '/tools/rate-limits',
        schema: aiToolRateLimitSchema,
        requestMethod: POST,
        payload: data,
      },
    });
  };
}

/**
 * Update a rate limit
 * PATCH /tools/rate-limits/{id}
 */
export function updateAIToolRateLimit(id: string | number, data: UpdateRateLimitPayload) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.UPDATE_AI_TOOL_RATE_LIMIT_REQUEST,
          actionTypes.UPDATE_AI_TOOL_RATE_LIMIT_SUCCESS,
          actionTypes.UPDATE_AI_TOOL_RATE_LIMIT_FAILURE,
        ],
        endpoint: `/tools/rate-limits/${id}`,
        schema: aiToolRateLimitSchema,
        requestMethod: PATCH,
        payload: data,
      },
    });
  };
}

/**
 * Delete a rate limit
 * DELETE /tools/rate-limits/{id}
 */
export function deleteAIToolRateLimit(id: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.DELETE_AI_TOOL_RATE_LIMIT_REQUEST,
          actionTypes.DELETE_AI_TOOL_RATE_LIMIT_SUCCESS,
          actionTypes.DELETE_AI_TOOL_RATE_LIMIT_FAILURE,
        ],
        endpoint: `/tools/rate-limits/${id}`,
        requestMethod: DELETE,
        requestInfo: { id },
      },
    });
  };
}

// ============================================================================
// MCP Config Actions
// ============================================================================

/**
 * Get MCP server configuration for a package
 * GET /tool-packages/{id}/mcp-config
 */
export function fetchAIToolMCPConfig(packageId: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.GET_AI_TOOL_MCP_CONFIG_REQUEST,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_SUCCESS,
          actionTypes.GET_AI_TOOL_MCP_CONFIG_FAILURE,
        ],
        endpoint: `/tool-packages/${packageId}/mcp-config`,
        requestMethod: GET,
        requestInfo: { packageId },
      },
    });
  };
}

/**
 * Regenerate MCP API key for a package
 * POST /tool-packages/{id}/mcp-config/regenerate-key
 */
export function regenerateAIToolMCPKey(packageId: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.REGENERATE_AI_TOOL_MCP_KEY_REQUEST,
          actionTypes.REGENERATE_AI_TOOL_MCP_KEY_SUCCESS,
          actionTypes.REGENERATE_AI_TOOL_MCP_KEY_FAILURE,
        ],
        endpoint: `/tool-packages/${packageId}/mcp-config/regenerate-key`,
        requestMethod: POST,
        requestInfo: { packageId },
      },
    });
  };
}

/**
 * Test MCP server connection
 * POST /tool-packages/{id}/mcp-config/test
 */
export function testAIToolMCPConnection(packageId: string | number) {
  return (dispatch: any) => {
    return dispatch({
      [CALL_GENAI_API]: {
        types: [
          actionTypes.TEST_AI_TOOL_MCP_CONNECTION_REQUEST,
          actionTypes.TEST_AI_TOOL_MCP_CONNECTION_SUCCESS,
          actionTypes.TEST_AI_TOOL_MCP_CONNECTION_FAILURE,
        ],
        endpoint: `/tool-packages/${packageId}/mcp-config/test`,
        requestMethod: POST,
        requestInfo: { packageId },
      },
    });
  };
}

// ============================================================================
// AI Compatible Nexsets Actions (for NexsetSelector)
// ============================================================================

/**
 * Which set of Nexsets the tool picker is showing.
 *
 * - `owned`: the caller's own Nexsets (plus anything their ACL mode grants).
 *   Unchanged from the original behaviour.
 * - `marketplace`: Nexsets listed on the Marketplace that the caller can reach
 *   by any role — owner, collaborator or sharer alike.
 *
 * The two sets OVERLAP: a Data Product you own appears under both. That is
 * intended. The segment answers "is this listed on the Marketplace", not "did
 * someone grant it to me", so hiding your own listings would make the Data
 * Products tab lie about what is published.
 */
export type AICompatibleNexsetSource = 'owned' | 'marketplace';

export interface LoadAICompatibleNexsetsParams {
  page: number;
  sortCriteria?: string;
  aclQueryMode?: string;
  source?: AICompatibleNexsetSource;
}

/**
 * Load nexsets that can back an AI tool.
 * This action is isolated from the main nexset listing to avoid conflicts.
 * Uses the AI_COMPATIBLE_NEXSETS view for filters (same filter types as MY_DATA_SETS).
 *
 * `marketplace_eligible=true` means the Nexset has a Nexset/Sync API
 * (`nexset_api_compatible && !agentic` in admin-api) — i.e. a tool can actually
 * read it. It says nothing about the Marketplace.
 *
 * In `marketplace` mode two further params narrow to listed Data Products the
 * caller can reach: `marketplace_published=true` (an active marketplace_items
 * row) and `access_role=all`.
 *
 * `all` — not `sharer` — because the two backends disagree on the unsuffixed
 * role. Rails special-cases it (user.rb:770 -> DataSet.accessible(:sharer) ->
 * accessible_non_owner), so `sharer` there means sharer-ONLY and drops the
 * caller's own listings; FastAPI reads it as "sharer or better" and keeps them.
 * Both rewrite `all` to sharer-or-better *through the ordinary path*
 * (accessible.rb:241, access_control.py:1101), so it is the one value that
 * spans every role identically on either backend.
 *
 * NOTE: this is deliberately not an approval discriminator. Admin-api records
 * no durable link between an approved access request and the ACL it writes, and
 * `access_roles` reports only the caller's *highest* role, so a Marketplace
 * grant is invisible whenever the caller also holds a stronger one. Every row
 * here is published; none of them is labelled "approved" in the UI.
 */
export function loadAICompatibleNexsets(params: LoadAICompatibleNexsetsParams) {
  return (dispatch: any, getState: any) => {
    const { page, sortCriteria = Constants.SortCriteria.LAST_CREATED_DESC } = params;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];

    const {
      app: {
        accessControl,
        visibilityFilters: { availableFilters: allAvailableFilters, filters },
      },
    } = getState();

    const aclQueryMode = params.aclQueryMode || accessControl.mode;

    const availableFilterTypes =
      AvailableFilterTypesByView[SearchFilterViews.AI_COMPATIBLE_NEXSETS];
    const availableFilters = pick(allAvailableFilters, availableFilterTypes);

    const currentFilters = filters[SearchFilterViews.AI_COMPATIBLE_NEXSETS] || [];
    // Always constrain to active nexsets server-side. Previously the selector
    // fetched every status and dropped inactive ones client-side, which broke
    // pagination counts (pages showing < per_page rows, wrong totals).
    const activeStatusFilter = {
      token: SearchFilterTypes.STATUS,
      operator: SearchFilterOpTypes.EQUALS,
      value: Constants.SourceStatus.ACTIVE.toLowerCase(),
    };
    const { processedFilters } = processSearchFilters(
      [activeStatusFilter, ...currentFilters],
      availableFilters,
    );

    const isMarketplace = params.source === 'marketplace';

    let requestMethod: string;
    let endpoint: string;
    let payload: Record<string, any>;

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/data_sets';
      payload = {
        page,
        per_page: Constants.LISTING_ITEMS_PER_PAGE,
        sort_by: sortKey,
        sort_order: sortOrder,
        marketplace_eligible: true,
        // Spread last so these win over the `access_role: 'collaborator'` that
        // adminApi injects for MODE_COLLABORATOR.
        ...(isMarketplace ? { marketplace_published: true, access_role: 'all' } : {}),
      };
    } else {
      requestMethod = POST;
      const marketplaceParams = isMarketplace ? '&marketplace_published=true&access_role=all' : '';
      endpoint = `/data_sets/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}&marketplace_eligible=true${marketplaceParams}`;
      payload = {
        filters: processedFilters,
      };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_AI_COMPATIBLE_NEXSETS_REQUEST,
          actionTypes.GET_AI_COMPATIBLE_NEXSETS_SUCCESS,
          actionTypes.GET_AI_COMPATIBLE_NEXSETS_FAILURE,
        ],
        endpoint,
        schema: [dataSetSchema],
        requestMethod,
        payload,
        aclQueryMode,
        requestInfo: {
          view: SearchFilterViews.AI_COMPATIBLE_NEXSETS,
          source: params.source ?? 'owned',
        },
      },
    });
  };
}
